import React, { useState } from 'react';
import { AlertTriangle, Ban, X } from 'lucide-react';

/**
 * Lo que el backend responde con 409 al intentar editar una factura cuyos
 * lotes ya tuvieron consumo (ver `editarFactura`, PROMPT_N°049).
 */
export interface LoteConsumido {
  lote_id: number;
  numero_lote: string;
  ingrediente: string;
  consumido_base: number;
  movimientos_salida: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  numeroFactura: string;
  /** Mensaje tal cual lo manda el backend. */
  mensaje: string;
  lotes: LoteConsumido[];
  /** Dispara el flujo de anulación existente. */
  onAnular: () => Promise<void>;
}

/**
 * Explica por qué una factura no se puede editar y ofrece la única salida.
 *
 * Editar una factura es reemplazarla: se borran sus lotes y sus movimientos
 * y se vuelven a crear. Mientras nadie haya consumido de esos lotes eso es
 * reversible; en cuanto salió mercadería, rehacerlos destruiría la
 * trazabilidad de ese consumo. Por eso el backend corta con 409 y deriva a
 * anular + registrar de nuevo, que sí deja la historia completa.
 *
 * Antes este 409 llegaba a la pantalla como un error genérico y el usuario
 * no tenía forma de saber qué lote lo bloqueaba ni qué hacer al respecto.
 */
const ModalFacturaLotesConsumidos: React.FC<Props> = ({
  isOpen,
  onClose,
  numeroFactura,
  mensaje,
  lotes,
  onAnular,
}) => {
  const [confirmando, setConfirmando] = useState(false);
  const [anulando, setAnulando] = useState(false);

  if (!isOpen) return null;

  const handleAnular = async () => {
    setAnulando(true);
    try {
      await onAnular();
    } finally {
      setAnulando(false);
      setConfirmando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4 flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 p-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                No se puede editar la factura {numeroFactura}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                Ya salió mercadería de sus lotes.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            disabled={anulando}
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* El mensaje del backend, textual */}
          <p className="text-sm text-gray-700 dark:text-gray-300">{mensaje}</p>

          {/* Qué lotes y cuánto salió */}
          {lotes.length > 0 && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Lote
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Ingrediente
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Consumido
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Salidas
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {lotes.map((l) => (
                    <tr key={l.lote_id}>
                      <td className="px-4 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">
                        {l.numero_lote}
                      </td>
                      <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                        {l.ingrediente}
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-amber-700 dark:text-amber-400">
                        {Number(l.consumido_base).toLocaleString('es-CL')}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">
                        {l.movimientos_salida}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-4 text-sm text-gray-600 dark:text-gray-300">
            Anular revierte el stock que esta factura había ingresado y deja la
            factura en el historial como anulada. Después podés registrarla de
            nuevo con el mismo número y los datos corregidos.
          </div>

          {/* Confirmación en dos pasos: anular mueve stock. */}
          {confirmando ? (
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 space-y-3">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                ¿Anular la factura {numeroFactura}? Se revierte el stock que
                ingresó.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmando(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  disabled={anulando}
                >
                  No, volver
                </button>
                <button
                  type="button"
                  onClick={handleAnular}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={anulando}
                >
                  {anulando ? 'Anulando…' : 'Sí, anular'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={() => setConfirmando(true)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Ban className="h-4 w-4" />
                Anular factura
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalFacturaLotesConsumidos;
